import { CheckIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

/** A row in an {@link OptionsPanel}. */
export interface OptionsPanelOption {
  label: string;
  value: string;
  /** Secondary grey line under the label (category · price, address, …). */
  description?: string;
  disabled?: boolean;
}

export interface OptionsPanelProps {
  /** Currently selected option values (controlled). */
  value: string[];
  onChange: (value: string[]) => void;
  options: OptionsPanelOption[];
  /** Label for the "select all" header row; the header hides when omitted. */
  selectAllLabel?: string;
  /** Announced name for the list (the panel has no visible title in the design). */
  'aria-label'?: string;
  className?: string;
}

/**
 * Standalone multi-select list panel (Figma 358-12445) — a white card of
 * numbered rows, each with a name, an optional grey subtitle and a checkbox on
 * the right, above them a "Выбрать все" toggle.
 *
 * Purely presentational and fully controlled; the caller decides where it sits
 * (docked beside a modal, in a drawer, inline).
 */
export function OptionsPanel({
  value,
  onChange,
  options,
  selectAllLabel,
  className,
  ...aria
}: OptionsPanelProps) {
  const selectable = options.filter((option) => !option.disabled);
  const allSelected =
    selectable.length > 0 && selectable.every((option) => value.includes(option.value));

  function toggle(optionValue: string): void {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  }

  function toggleAll(): void {
    onChange(allSelected ? [] : selectable.map((option) => option.value));
  }

  return (
    <div
      className={cn('flex flex-col gap-4 rounded-3xl bg-card p-6 shadow-lg', className)}
    >
      {selectAllLabel ? (
        <button
          type="button"
          onClick={toggleAll}
          aria-pressed={allSelected}
          className="flex shrink-0 items-center gap-2 rounded-lg text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <PanelCheckbox checked={allSelected} />
          <span className="text-base font-medium tracking-tight text-foreground">
            {selectAllLabel}
          </span>
        </button>
      ) : null}

      <ul
        aria-label={aria['aria-label']}
        aria-multiselectable="true"
        role="listbox"
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
      >
        {options.map((option, index) => {
          const isSelected = value.includes(option.value);
          return (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={option.disabled ?? false}
                onClick={() => toggle(option.value)}
                className={cn(
                  // Figma row: 60px tall, 16px radius, 1px gray-200 border.
                  'flex h-[60px] w-full items-center gap-2 rounded-2xl border border-input bg-card px-3 text-left transition-colors outline-none',
                  'hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  'disabled:pointer-events-none disabled:opacity-50',
                )}
              >
                <span className="flex size-9 shrink-0 items-center justify-center text-sm font-semibold text-foreground">
                  {index + 1}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate text-sm font-medium tracking-tight text-foreground">
                    {option.label}
                  </span>
                  {option.description ? (
                    <span className="truncate text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  ) : null}
                </span>
                <PanelCheckbox checked={isSelected} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The panel's 24px checkbox glyph (Figma 24-6064): an 8px-radius square with a
 * 2px border, filled with the primary colour once checked. Rendered as a span —
 * the enclosing row owns the click and the a11y state.
 */
function PanelCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-[8px] border-2 transition-colors',
        checked
          ? 'border-control-bg-checked bg-control-bg-checked'
          : 'border-control-border bg-transparent',
      )}
    >
      {checked ? <CheckIcon className="size-3.5 text-control-fg-checked!" /> : null}
    </span>
  );
}
